import { Box, Skeleton } from "@mui/material";
import { type FC, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { CollapsibleSection, CollapsibleSectionVariant } from "../../../components/UIToolkit/Panels/CollapsibleSection";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { CONTROL_HEIGHT } from "../../../theme/tokens";
import { PRINTOUT_LIST_CARD_SX } from "../printoutPresetRowStyles";
import { CustomPrintoutListActions } from "./CustomPrintoutListActions";
import { CustomPrintoutListEmpty } from "./CustomPrintoutListEmpty";
import { CustomPrintoutRadioRows } from "./CustomPrintoutRadioRows";
import { NEW_PRINTOUT_ROW_KEY } from "./consts";
import { type CustomPrintoutSummary, MAX_CUSTOM_PRINTOUTS } from "./customPrintoutTypes";

type Props = {
	status: "pending" | "error" | "success";
	printouts: CustomPrintoutSummary[];
	/** `null` = a new, unsaved printout. */
	selectedId: string | null;
	/** The shown printout has unsaved work (a new one then gets its own row until it is saved). */
	isDirty: boolean;
	/** Can design, rename and delete (not read-only here). */
	canEdit: boolean;
	/** Annie is designing: switching waits for her answer. */
	locked: boolean;
	onSelect: (printoutId: string) => void;
	onNew: () => void;
	/** Starts a new printout from the chosen saved one. */
	onNewFromSelected: () => void;
	/** The new printout shown starts from a saved one (it then gets its own row before anything is designed). */
	isStartedFromSaved: boolean;
	onRename: () => void;
	onDelete: () => void;
	onRetry: () => void;
	/** Puts an example request into the design chat. */
	onUseSuggestion: (prompt: string) => void;
	/** The chosen saved printout's version history control; `null` while a new printout is shown. */
	versionHistory: ReactNode;
};

/**
 * The department's custom printouts, picked the way the Detailed printout's saved printouts are: a radio list
 * whose chosen row carries the brand wash. The list collapses to its header, which then names the chosen printout;
 * the actions under it stay: the chosen printout's version history for everyone, and New, Rename and Delete for people
 * who can edit.
 */
export const CustomPrintoutList: FC<Props> = (props) => {
	const { status, printouts, selectedId, isDirty, canEdit, locked, isStartedFromSaved } = props;
	const { t } = useTranslation();
	const labelId = useId();
	const [isOpen, setIsOpen] = useState(false);
	const rows = [
		...printouts.map((printout) => ({ key: printout.id, name: printout.name, isSummary: printout.coverage === "summary" })),
		...(selectedId === null && (isDirty || isStartedFromSaved)
			? [{ key: NEW_PRINTOUT_ROW_KEY, name: t("printout.custom.ai.new.name"), isSummary: false }]
			: []),
	];
	const selectedKey = selectedId === null ? NEW_PRINTOUT_ROW_KEY : selectedId;
	// Open, the count; collapsed, the chosen printout, which the hidden radio list would otherwise show.
	const headerSummary = isOpen
		? printouts.length > 0
			? t("printout.custom.ai.list.count", { count: printouts.length, max: MAX_CUSTOM_PRINTOUTS })
			: undefined
		: rows.find((row) => row.key === selectedKey)?.name;
	return (
		<Box data-testid="custom-printout-list" sx={[PRINTOUT_LIST_CARD_SX, { display: "flex", flexDirection: "column", gap: 1 }]}>
			<CollapsibleSection
				variant={CollapsibleSectionVariant.Eyebrow}
				title={t("printout.custom.ai.list.label")}
				titleId={labelId}
				summary={headerSummary}
				open={isOpen}
				onToggle={() => setIsOpen((wasOpen) => !wasOpen)}
				dataTestId="custom-printout-list-toggle"
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{status === "pending" && <Skeleton variant="rounded" height={CONTROL_HEIGHT.COMPACT} aria-hidden data-testid="custom-printout-list-loading" />}
					{status === "error" && (
						<InlineHint
							tone="warning"
							dataTestId="custom-printout-list-error"
							actionPlacement="below"
							action={
								<SystemButton data-testid="custom-printout-list-retry" buttonVariant="borderless" size="small" onClick={props.onRetry}>
									{t("retry")}
								</SystemButton>
							}
						>
							{t("printout.custom.ai.list.failed")}
						</InlineHint>
					)}
					{status === "success" && rows.length === 0 && <CustomPrintoutListEmpty canEdit={canEdit} onUseSuggestion={props.onUseSuggestion} />}
					{rows.length > 0 && (
						<CustomPrintoutRadioRows rows={rows} labelId={labelId} selectedKey={selectedKey} isDirty={isDirty} locked={locked} onSelect={props.onSelect} />
					)}
				</Box>
			</CollapsibleSection>
			{status === "success" && (canEdit || props.versionHistory !== null) && (
				<CustomPrintoutListActions
					canEdit={canEdit}
					versionHistory={props.versionHistory}
					printoutCount={printouts.length}
					selected={printouts.find((printout) => printout.id === selectedId)}
					isOnFreshNew={selectedId === null && !isDirty && !isStartedFromSaved}
					locked={locked}
					onNew={props.onNew}
					onNewFromSelected={props.onNewFromSelected}
					onRename={props.onRename}
					onDelete={props.onDelete}
				/>
			)}
		</Box>
	);
};
